import React, { useState, useEffect, useRef } from "react";
import { Helmet } from "react-helmet";
import api from "../utils/api";
import ReviewsBar from "../components/ReviewsBar";
import LiveStrip from "../components/LiveStrip";
import "../styles/parkingVsTaxi.css";

export default function HeathrowDropoff() {
  const [form, setForm] = useState({
    terminal: "T5",
      journey_type: "DROPOFF", // kept for payload
      planned_minutes: 8,
      vehicle_type: "car", // kept for payload
      blue_badge: false,
      blue_badge_registered: false,
      overheight: false
  });

  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const autoSubmitted = useRef(false);
  const changeTimer = useRef(null);

  // Prefill defaults + auto-run (same style as your Train tool)
  useEffect(() => {
    setForm(prev => ({
      ...prev,
      terminal: "T5",
      journey_type: "DROPOFF",
      planned_minutes: 8,
      vehicle_type: "car",
      blue_badge: false,
      blue_badge_registered: false,
      overheight: false
    }));
  }, []);

  useEffect(() => {
    if (autoSubmitted.current) return;
    // run once after initial prefill
    autoSubmitted.current = true;
    submit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!autoSubmitted.current) return;
    if (changeTimer.current) clearTimeout(changeTimer.current);
    changeTimer.current = setTimeout(() => {
      submit();
    }, 320);
    return () => {
      if (changeTimer.current) clearTimeout(changeTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);

  const submit = async () => {
    setLoading(true);
    setError(null);

    try {
      const payload = {
        ...form,
        planned_minutes: Math.min(10080, Math.max(1, Number(form.planned_minutes) || 8)) // 7 days cap
      };

      const res = await api.post("/api/heathrow-dropoff/calc", payload);
      setResult(res.data);
    } catch (err) {
      setError("Failed to calculate drop-off costs. Please try again.");
    }

    setLoading(false);
  };

  const money = v => (v == null ? "—" : `£${Number(v).toFixed(2)}`);

  const badge = (text, tone = "muted") => (
    <span
      style={{
        display: "inline-block",
        padding: "2px 8px",
        borderRadius: 999,
        fontSize: 12,
        marginLeft: 8,
        border: "1px solid #e5e7eb",
        background: tone === "warn" ? "#fff7ed" : tone === "ok" ? "#ecfdf5" : "#f9fafb",
        color: "#111827"
      }}
    >
      {text}
    </span>
  );

  const optionLabel = (id) => {
    if (id === "DROPOFF_ZONE") return "Terminal drop-off";
    if (id === "SHORT_STAY") return "Short Stay parking";
    if (id === "PARK_RIDE") return "Park & Ride / Long Stay";
    return id || "—";
  };

  const optionTagline = (id) => {
    if (id === "DROPOFF_ZONE") return "Fastest forecourt stop (PCN risk if long stay)";
    if (id === "SHORT_STAY") return "Best for pick-ups / waiting near terminal";
    if (id === "PARK_RIDE") return "Includes shuttle transfer to terminal (10–12 mins)";
    return "Compare by price & hassle";
  };

  const clampMinutes = (value) => {
    const num = Number(value);
    if (Number.isNaN(num)) return 8;
    return Math.min(10080, Math.max(1, num));
  };

  const renderMedalHero = () => {
    const paletteByIntent = {
      DROPOFF_ZONE: { bg: "#fef3c7", text: "#78350f", border: "#fcd34d" }, // amber
      SHORT_STAY: { bg: "#e0f2fe", text: "#0f172a", border: "#bae6fd" }, // blue
      PARK_RIDE: { bg: "#ecfdf3", text: "#065f46", border: "#bbf7d0" } // green
    };

    const options = Array.isArray(result?.options) ? result.options : [];
    const numMinutes = (() => {
      const fromResult = Number(result?.journey?.planned_minutes);
      if (Number.isFinite(fromResult)) return fromResult;
      const fromForm = Number(form.planned_minutes);
      if (Number.isFinite(fromForm)) return fromForm;
      return 0;
    })();

    const byId = options.reduce((acc, opt) => ({ ...acc, [opt.id]: opt }), {});
    const isExempt =
      form.blue_badge ||
      form.blue_badge_registered ||
      ["emergency", "recovery", "military"].includes(String(form.vehicle_type || "").toLowerCase());

    const baseOption = (id) => {
      const raw = byId[id];
      if (!raw) return null;
      const warnings = Array.isArray(raw.warnings) ? [...raw.warnings] : [];
      const infoNotes = [];

      if (id === "DROPOFF_ZONE" && !isExempt && numMinutes > 0) {
        warnings.unshift("PCN risk applies if you exceed the forecourt allowance for your terminal; charges and limits vary by terminal—follow signage.");
      }
      if (id === "PARK_RIDE") {
        // Tagline already mentions shuttle; avoid duplicate warning line.
      }
      if (id === "DROPOFF_ZONE" && isExempt) {
        infoNotes.push("♿ Accessibility priority: Blue Badge / exempt vehicles can use the forecourt.");
        infoNotes.push("ℹ️ Blue Badge holders are exempt from standard drop-off time limits at this terminal. Display badge clearly.");
      }
      return {
        ...raw,
        id,
        label: optionLabel(id),
        tagline: optionTagline(id),
        cost: Number.isFinite(Number(raw.cost)) ? Number(raw.cost) : null,
        warnings,
        infoNotes,
        isExempt: id === "DROPOFF_ZONE" && isExempt
      };
    };

    const dropoff = baseOption("DROPOFF_ZONE");
    const park = baseOption("PARK_RIDE");
    const shortStay = baseOption("SHORT_STAY");

    const medalDecision = () => {
      let copy = "Cheapest option overall.";

      if (dropoff?.isExempt) {
        const cheapestOther =
          [park, shortStay]
            .filter(Boolean)
            .sort((a, b) => (a.cost ?? Number.POSITIVE_INFINITY) - (b.cost ?? Number.POSITIVE_INFINITY))[0] || park || shortStay || null;
        const remaining =
          [park, shortStay].filter((o) => o && o.id !== cheapestOther?.id)[0] || (!cheapestOther && park) || (!cheapestOther && shortStay) || dropoff;
        copy = "Accessibility priority: forecourt kept as Gold for Blue Badge / exempt vehicles.";
        return {
          gold: dropoff,
          silver: cheapestOther || dropoff,
          bronze: remaining || dropoff,
          copy
        };
      }

      // 0-10: drop-off gold, park silver, short bronze
      if (numMinutes > 0 && numMinutes <= 10) {
        copy = "Best for quick stops under 10 minutes.";
        return {
          gold: dropoff || park || shortStay,
          silver: park || shortStay || dropoff,
          bronze: shortStay || park || dropoff,
          copy
        };
      }

      // 10-30: short stay preferred (even if Park & Ride is cheaper) to avoid shuttle hassle
      if (numMinutes > 10 && numMinutes <= 30) {
        copy = "Short Stay preferred for 11–30 mins; forecourt PCN risk after 10 mins.";
        return {
          gold: shortStay || park || dropoff,
          silver: park || dropoff || shortStay,
          bronze: dropoff || park || shortStay,
          copy
        };
      }

      // 30+ : dynamic between park and short; drop-off stays bronze
      const parkCost = park?.cost ?? Number.POSITIVE_INFINITY;
      const shortCost = shortStay?.cost ?? Number.POSITIVE_INFINITY;
      const hasPark = Number.isFinite(parkCost);
      const hasShort = Number.isFinite(shortCost);
      const priceGap = hasPark && hasShort ? shortCost - parkCost : null;
      const preferShort = hasShort && (!hasPark || priceGap <= 5 || shortCost <= parkCost * 1.15);

      if (preferShort) {
        copy = "Short Stay wins on convenience when price gap is small.";
        return {
          gold: shortStay || park || dropoff,
          silver: park || shortStay || dropoff,
          bronze: dropoff || park || shortStay,
          copy
        };
      }

      copy = "Park & Ride wins on price for longer waits.";
      return {
        gold: park || shortStay || dropoff,
        silver: shortStay || park || dropoff,
        bronze: dropoff || shortStay || park,
        copy
      };
    };

    const { gold, silver, bronze } = medalDecision();
    const recommendedOpt = gold || dropoff || shortStay || park;
    const recommendedId = recommendedOpt?.id || dropoff?.id;
    const cardLineup = (() => {
      const list = [];
      if (dropoff) list.push({ opt: dropoff, label: "Terminal drop-off (Forecourt)", tone: "DROPOFF_ZONE" });
      if (shortStay) list.push({ opt: shortStay, label: "Short Stay parking", tone: "SHORT_STAY" });
      if (park) list.push({ opt: park, label: "Park & Ride / Long Stay", tone: "PARK_RIDE" });
      return list;
    })();

    const heroColors = {
      bg: "#0b1224",
      text: "#fff",
      border: "1px solid #0f172a",
      shadow: "0 10px 25px rgba(0,0,0,0.16)"
    };

    return (
      <div
        style={{
          borderRadius: 18,
          padding: 18,
          marginBottom: 16,
          color: heroColors.text,
          background: heroColors.bg,
          border: heroColors.border,
          boxShadow: heroColors.shadow
        }}
      >
        <div style={{ fontWeight: 800, fontSize: 13, letterSpacing: 0.4, textTransform: "uppercase", opacity: 0.9 }}>
          Heathrow Drop-off (2026)
        </div>
        <div style={{ fontSize: 22, fontWeight: 800, marginTop: 6 }}>
          {recommendedId === "DROPOFF_ZONE"
            ? `Recommendation: Use Terminal drop-off (${money(dropoff?.cost)})`
            : recommendedId === "SHORT_STAY"
            ? `Recommendation: Use Short Stay (${money(shortStay?.cost)})`
            : recommendedId === "PARK_RIDE"
            ? `Recommendation: Use Long Stay (${money(park?.cost)})`
            : "Recommendation based on your stay"}
        </div>
        <div style={{ fontSize: 14, marginTop: 6, opacity: 0.9 }}>
          Drop-off cost for your stay: <strong>{money(dropoff?.cost)}</strong>
        </div>
        <div style={{ fontSize: 14, marginTop: 6, opacity: 0.9 }}>
          {recommendedId === "SHORT_STAY"
            ? `Reason: For ${numMinutes} mins, ${shortStay?.label || "Short Stay"} is cheaper than drop-off and avoids post-payment/PCN risk.`
            : recommendedId === "PARK_RIDE"
            ? `Reason: For ${numMinutes} mins, ${park?.label || "Long Stay"} is cheaper than Short Stay and avoids forecourt PCN risk. Includes shuttle transfer (10–12 mins).`
            : `Reason: For ${numMinutes} mins, ${dropoff?.label || "Terminal drop-off"} is fastest; pay by midnight next day to avoid PCN.`}
        </div>
        <div
          style={{
            marginTop: 12,
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
            gap: 10
          }}
        >
          {cardLineup.map(({ opt, label, tone }, idx) => {
            const paletteFor = paletteByIntent[opt?.id] || paletteByIntent[tone] || { bg: "#0b172a", text: "#0f172a", border: "#1f2937" };
            return (
              <div
                key={`${tone}-${idx}`}
                style={{
                  border: `1px solid ${paletteFor.border}`,
                  borderRadius: 14,
                  padding: 14,
                  background: paletteFor.bg,
                  color: paletteFor.text,
                  display: "flex",
                  flexDirection: "column",
                  gap: 6,
                  minHeight: 140
                }}
              >
                <div style={{ fontWeight: 800, fontSize: 13, letterSpacing: 0.5, color: paletteFor.text }}>
                  {label}
                  {opt?.id === recommendedId && (
                    <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 800, padding: "2px 8px", borderRadius: 999, background: "#0f172a", color: "#fff" }}>
                      Recommended
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 16, fontWeight: 800, color: paletteFor.text }}>
                  {opt?.cost != null ? money(opt.cost) : "—"}
                </div>
                <div style={{ fontSize: 12, color: "#111827", lineHeight: 1.4 }}>
                  {opt?.tagline || "Compare safety, speed, and price."}
                  {Array.isArray(opt?.warnings) && opt.warnings[0] && (
                    <div style={{ fontSize: 12, color: paletteFor.text, marginTop: 4 }}>{`⚠️ ${opt.warnings[0]}`}</div>
                  )}
                </div>
                {Array.isArray(opt?.infoNotes) &&
                  opt.infoNotes.map((n, idx) => (
                    <div key={idx} style={{ fontSize: 12, color: "#111827" }}>
                      {n}
                    </div>
                  ))}
                <div style={{ marginTop: "auto" }}>
                  <button
                    type="button"
                    onClick={() => {
                      const map = {
                        DROPOFF_ZONE: "dropoff_zone",
                        SHORT_STAY: "short_stay",
                        PARK_RIDE: "park_ride"
                      };
                      const key = map[opt?.id];
                      if (!key) return;
                      const el = document.getElementById(`collapse-${key}`);
                      if (el) {
                        el.open = true;
                        requestAnimationFrame(() => {
                          el.scrollIntoView({ behavior: "smooth", block: "start" });
                        });
                      }
                    }}
                    style={{
                      marginTop: 8,
                      background: "transparent",
                      border: `1px solid ${paletteFor.border}`,
                      color: paletteFor.text,
                      padding: "6px 10px",
                      borderRadius: 10,
                      cursor: "pointer",
                      fontWeight: 700,
                      fontSize: 12
                    }}
                  >
                    View details ▸
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        <div style={{ marginTop: 12 }}>
          {result?.taxi_alternative && (
            <div style={{ padding: 10, borderRadius: 10, border: "1px solid #e5e7eb", background: "#f8fafc", color: "#0f172a" }}>
              <div style={{ fontWeight: 700, marginBottom: 2, fontSize: 12 }}>💡 {result.taxi_alternative.title}</div>
              <div style={{ fontSize: 12, color: "#374151" }}>
                {result.taxi_alternative.message}{" "}
                <a
                  href={result.taxi_alternative.cta || "/heathrow-fare-compare"}
                  style={{ fontWeight: 700, color: "#0f172a", textDecoration: "underline" }}
                >
                  Compare taxi fares →
                </a>
              </div>
            </div>
          )}
        <div style={{ fontSize: 12, color: heroColors.text, opacity: heroColors.text === "#fff" ? 0.85 : 0.9, marginTop: 6 }}>
            Terminal: <strong>{result.journey?.terminal}</strong> • Type: <strong>{result.journey?.journey_type}</strong> • Planned time:{" "}
            <strong>{result.journey?.planned_minutes} mins</strong>
            {result.journey?.overheight ? " • Overheight" : ""}
            {result.journey?.blue_badge ? " • Blue Badge" : ""}
          </div>
        </div>
      </div>
    );
  };

  const renderOptionCard = (opt) => {
    const isBest = result?.recommendation?.best === opt.id;

    const linkBlocks = {
      DROPOFF_ZONE: [
        { label: "Pay drop-off charge (£7)", href: "https://heathrowdropoff.apcoa.com/trip/vrn" },
        { label: "Pay or appeal a PCN", href: "https://heathrowdropoffpcnpayments.apcoa.com/pcnonline/index.php" },
        { label: "Blue Badge discount / registration", href: "https://heathrowdropoff.apcoa.com/account/login?returnUrl=%2Fbluebadgetrip%2Fvrn" },
        { label: "Pay by phone: 0330 008 5600", href: "tel:03300085600" }
      ],
      PARK_RIDE: [
        { label: "Long Stay / Park & Ride info", href: "https://www.heathrow.com/transport-and-directions/heathrow-parking" }
      ],
      SHORT_STAY: [
        { label: "Short Stay parking prices", href: "https://www.heathrow.com/transport-and-directions/heathrow-parking/heathrow-short-stay-parking" }
      ]
    };

    return (
      <div
        key={opt.id}
        style={{
          border: "1px solid #e5e7eb",
          borderRadius: 12,
          padding: 14,
          marginBottom: 12,
          background: isBest ? "#ecfdf5" : "#fff"
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
          <div style={{ fontWeight: 800 }}>
            {opt.title}
            {isBest && badge("Recommended", "ok")}
          </div>
          <div style={{ fontWeight: 800 }}>{money(opt.cost)}</div>
        </div>

        {opt.best_for?.length > 0 && (
          <div style={{ marginTop: 6, fontSize: 13, color: "#374151" }}>
            Best for:{" "}
            <span style={{ color: "#111827" }}>
              {opt.best_for.includes("free_dropoff") ? "free / cheapest, " : ""}
              {opt.best_for.includes("pickup_best") ? "pick-ups, " : ""}
              {opt.best_for.includes("fastest") ? "fastest, " : ""}
              {opt.best_for.includes("waiting") ? "waiting, " : ""}
              {opt.best_for.includes("meet_and_greet_style") ? "meet & greet style, " : ""}
            </span>
          </div>
        )}

        {Array.isArray(opt.warnings) && opt.warnings.length > 0 && (
          <div style={{ marginTop: 10, background: "#fff7ed", border: "1px solid #fed7aa", padding: 10, borderRadius: 10 }}>
            <div style={{ fontWeight: 700, marginBottom: 6 }}>⚠️ Important</div>
            <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.5 }}>
              {opt.warnings.map((w, i) => <li key={i}>{w}</li>)}
            </ul>
          </div>
        )}

        {Array.isArray(opt.notes) && opt.notes.length > 0 && (
          <div style={{ marginTop: 10 }}>
            <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.5, color: "#374151" }}>
              {opt.notes.map((n, i) => <li key={i}>{n}</li>)}
            </ul>
          </div>
        )}

        {linkBlocks[opt.id] && (
          <div style={{ marginTop: 10 }}>
            <div style={{ fontWeight: 700, marginBottom: 6 }}>Next steps</div>
            <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.5 }}>
              {linkBlocks[opt.id].map((link, i) => (
                <li key={i}>
                  <a href={link.href} target="_blank" rel="noreferrer">
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  };

  const renderOptionSection = (opt, title, defaultOpen = false) => {
    if (!opt) return null;
    return (
      <details id={`collapse-${opt.id.toLowerCase().replace(/_/g, "_")}`} className="train-collapsible" open={defaultOpen} style={{ marginTop: 12 }}>
        <summary className="train-summary-bar">{title}</summary>
        <div className="train-collapsible-body">{renderOptionCard(opt)}</div>
      </details>
    );
  };

  return (
    <div className="parking-wrapper">
      <Helmet>
        <title>Heathrow Drop-off (2026): Fees, Rules &amp; Payment Guide</title>

        <meta
          name="description"
          content="Heathrow drop-off rules for 2026 explained. See £7 terminal fees, Short Stay prices, Long Stay options, Blue Badge exemptions, and how to avoid PCNs."
        />

        <meta
          name="keywords"
          content="Heathrow drop off charge 2026, Heathrow drop off fee, Heathrow free drop off, Heathrow Park & Ride drop off, Heathrow short stay prices, Heathrow pickup parking cost, Heathrow 10 minute rule, Heathrow drop off PCN"
        />

        <link rel="canonical" href="https://www.cheapcabgrab.com/heathrow-dropoff" />

        <meta property="og:title" content="Heathrow Drop-off (2026): Fees, Rules &amp; Payment Guide" />
        <meta
          property="og:description"
          content="Heathrow drop-off rules for 2026 explained. See £7 terminal fees, Short Stay prices, Long Stay options, Blue Badge exemptions, and how to avoid PCNs."
        />
        <meta property="og:url" content="https://www.cheapcabgrab.com/heathrow-dropoff" />

        {/* FAQ Rich Snippet */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: [
              {
                "@type": "Question",
                name: "How much is the Heathrow terminal drop-off charge?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text:
                    "The Heathrow drop-off charge is £7 per visit as of January 2026. Payment is due by midnight the following day (online/phone/autopay). No pay machines at the terminal."
                }
              },
              {
                "@type": "Question",
                name: "Is there a free drop-off option at Heathrow?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text:
                    "Yes. Park & Ride (Long Stay) is free for drop-off. Drop passengers, then use the free bus to the terminal."
                }
              },
              {
                "@type": "Question",
                name: "What is the 10-minute rule at Heathrow terminal drop-off?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text:
                    "Forecourt drop-off has a 10-minute limit. Staying longer can trigger an £80 PCN (discounted to £40 if paid within 14 days)."
                }
              },
              {
                "@type": "Question",
                name: "Should I use Short Stay parking for pick-ups?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text:
                    "Yes. For pick-ups or waiting, use Terminal Parking (Short Stay). Forecourt drop-off is not suitable for waiting."
                }
              },
              {
                "@type": "Question",
                name: "How much is Heathrow Short Stay parking for a few hours?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text:
                    "Short Stay prices vary by duration. This calculator compares your planned time to Short Stay tariffs and surfaces the cheapest safe option."
                }
              },
              {
                "@type": "Question",
                name: "Do taxis and private-hire vehicles pay the Heathrow drop-off charge?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text:
                    "Yes. Taxis and private-hire vehicles pay £7 per visit (same as cars) unless a discount/exemption applies."
                }
              },
              {
                "@type": "Question",
                name: "Can Blue Badge holders get a discount for Heathrow drop-off?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text:
                    "Yes. Eligible Blue Badge holders can apply for a 100% discount by registering the visit/VRN. Always use the official process to avoid charges or PCNs."
                }
              },
              {
                "@type": "Question",
                name: "What does this Heathrow drop-off calculator check?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text:
                    "It compares the £7 forecourt charge against Park & Ride and Short Stay based on your planned time, and highlights key rules like the 10-minute limit, payment deadlines, and exemptions."
                }
              }
            ]
          })}
        </script>

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            name: "Heathrow Drop-off Guide (Forecourt vs Park & Ride vs Short Stay)",
            url: "https://www.cheapcabgrab.com/heathrow-dropoff",
            description:
              "Plan Heathrow forecourt drop-off vs Park & Ride (free) vs Short Stay parking with pricing, rules, and PCN/payment deadlines.",
            inLanguage: "en-GB",
            mainEntity: [
              {
                "@type": "Question",
                name: "Is Heathrow drop-off free anywhere?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text:
                    "Free drop-off is typically available at Heathrow Park & Ride (Long Stay), with a free transfer bus to the terminal. This tool compares your options by duration."
                }
              },
              {
                "@type": "Question",
                name: "What happens if I stay more than 10 minutes at Heathrow drop-off?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text:
                    "Staying longer than the terminal forecourt time limit can lead to a Parking Charge Notice (PCN). This tool highlights the risk based on your planned time."
                }
              },
              {
                "@type": "Question",
                name: "Where should I pick up passengers at Heathrow?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text:
                    "Pick-ups are usually made from Terminal Parking (Short Stay) or Park & Ride, not the terminal drop-off forecourts. This tool recommends the best option for pick-ups."
                }
              }
            ]
          })}
        </script>

        {/* Software Application schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            name: "Airport Drop-off Price Engine",
            applicationCategory: "TravelApplication",
            operatingSystem: "All",
            offers: {
              "@type": "Offer",
              price: "0",
              priceCurrency: "GBP"
            },
            description:
              "Dynamic calculator for UK airport drop-off fees. Re-ranks parking options in real-time based on minutes stayed."
          })}
        </script>
      </Helmet>

      <LiveStrip variant="heathrow" />

      <div className="parking-header">
        <h1>🚗 Heathrow Drop-off Guide (Forecourt vs Short Stay vs Park &amp; Ride)</h1>
        <p>
          Compare <strong>terminal drop-off charge</strong> vs <strong>Park &amp; Ride (free option)</strong> vs{" "}
          <strong>Short Stay</strong>. Includes 10-minute rule warnings, bus stop info, and overheight notes.
        </p>
        <p style={{ fontSize: 14, color: "#374151" }}>
          Check all options to drop off. <a href="/heathrow-dropoff-calculator">Use the drop-off calculator.</a>
        </p>
      </div>

      {/* FORM */}
      <div className="parking-form">
        <div className="form-grid-compact">
          <div className="form-group">
            <label>Terminal</label>
            <select
              className="parking-input"
              value={form.terminal}
              onChange={(e) => setForm({ ...form, terminal: e.target.value })}
            >
              <option value="T2">Terminal 2</option>
              <option value="T3">Terminal 3</option>
              <option value="T4">Terminal 4</option>
              <option value="T5">Terminal 5</option>
            </select>
          </div>
        </div>

        <div className="minutes-card" style={{ marginTop: 12 }}>
          <div className="minutes-label-row">
            <label style={{ margin: 0 }} htmlFor="planned_minutes">Duration of stay (mins)</label>
            <span className="minutes-chip">Time</span>
            </div>
            <div className="minutes-input-row">
              <input
                type="number"
                className="parking-input"
                min="1"
                max="10080"
                step="1"
                id="planned_minutes"
                value={form.planned_minutes}
                onChange={(e) =>
                  setForm({
                    ...form,
                    planned_minutes: e.target.value === "" ? "" : Math.min(10080, Math.max(1, Number(e.target.value)))
                  })
                }
                onBlur={() => {
                  setForm({ ...form, planned_minutes: clampMinutes(form.planned_minutes) });
                }}
              />
              <div className="minutes-nudge">
                <button
                  type="button"
                  onClick={() => setForm({ ...form, planned_minutes: clampMinutes((Number(form.planned_minutes) || 0) - 1) })}
                >
                  −
                </button>
                <button
                  type="button"
                  onClick={() => setForm({ ...form, planned_minutes: clampMinutes((Number(form.planned_minutes) || 0) + 1) })}
                >
                  +
                </button>
              </div>
            </div>
          </div>

        <button onClick={submit} className="parking-btn">
          {loading ? "Calculating…" : "Calculate best option"}
        </button>

        {error && <p style={{ color: "red", marginTop: 10 }}>{error}</p>}
      </div>

      {/* RESULTS */}
      {result && (
        <div className="parking-results">
          {renderMedalHero()}

          {(() => {
            const map = {};
            (Array.isArray(result.options) ? result.options : []).forEach((o) => {
              map[o.id] = o;
            });
            const drop = map["DROPOFF_ZONE"] || null;
            const park = map["PARK_RIDE"] || null;
            const shortStay = map["SHORT_STAY"] || null;

            return (
          <>
            {renderOptionSection(drop, "Terminal drop-off", false)}
            {renderOptionSection(shortStay, "Short Stay parking", false)}
            {renderOptionSection(park, "Park & Ride / Long Stay", false)}
          </>
        );
      })()}

          {Array.isArray(result.notices) && result.notices.length > 0 && (
            <div style={{ marginTop: 12, background: "#f9fafb", border: "1px solid #e5e7eb", padding: 12, borderRadius: 12 }}>
              <div style={{ fontWeight: 800, marginBottom: 8 }}>ℹ️ Notes</div>
              <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.5 }}>
                {result.notices.map((n, i) => <li key={i}>{n}</li>)}
              </ul>
            </div>
          )}

          <details className="train-collapsible">
            <summary className="train-summary-bar">ℹ️ What this tool checks</summary>
            <div className="train-collapsible-body">
              <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.5 }}>
                <li>Terminal drop-off charge vs Park &amp; Ride vs Short Stay.</li>
                <li>10-minute forecourt stay risk warning (drop-off zone).</li>
                <li>Blue Badge &amp; vehicle-type discounts/exemptions (where indicated).</li>
                <li>Overheight notes for some terminals.</li>
              </ul>
            </div>
          </details>

          <details className="train-collapsible" style={{ marginTop: 12 }}>
            <summary className="train-summary-bar">🆘 Need help with drop-off or parking?</summary>
            <div className="train-collapsible-body">
              <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.6 }}>
                <li>📞 Customer services: <a href="tel:03332007459">0333 200 7459</a></li>
                <li>📧 Refunds: <a href="mailto:refunds.heathrowdropoff@apcoa.com">refunds.heathrowdropoff@apcoa.com</a></li>
                <li>💬 Live webchat (official): <a href="https://heathrowdropoff.apcoa.com/help" target="_blank" rel="noreferrer">Start chat</a></li>
                <li>⏰ Hours: Mon–Fri 08:00–20:00 | Weekends 08:00–17:00</li>
              </ul>
            </div>
          </details>

          {/* Internal links (same pattern as Train tool) */}
          <details className="train-collapsible" style={{ marginTop: 12 }}>
            <summary className="train-summary-bar">✅ Why this calculator is reliable</summary>
            <div className="train-collapsible-body">
              <div style={{ border: "1px solid #e5e7eb", borderRadius: 12, padding: 12, background: "#f9fafb" }}>
                <p style={{ marginTop: 0, marginBottom: 6, fontSize: 14, color: "#374151" }}>
                  This tool uses the latest Heathrow and APCOA rules to compare terminal drop-off charges, Short Stay parking, and Park & Ride options. We factor in stay duration, PCN risk, vehicle type, Blue Badge exemptions, and terminal-specific rules so you can choose the safest and cheapest option.
                </p>
                <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.5, fontSize: 13, color: "#374151" }}>
                  <li>Updated regularly with official Heathrow tariffs.</li>
                  <li>Highlights the 10-minute forecourt rule and PCN risk.</li>
                  <li>Designed to surface the cheapest safe option for your stay.</li>
                </ul>
                <div style={{ marginTop: 8, fontSize: 12, color: "#6b7280" }}>
                  Rules and prices can change. Always confirm on official Heathrow/APCOA signage and payment pages before travel.
                </div>
              </div>
            </div>
          </details>

          <div style={{ marginTop: 14, border: "1px solid #e5e7eb", borderRadius: 12, padding: 12, background: "#fff" }}>
            <strong>Plan your Heathrow journey:</strong>
            <ul style={{ marginTop: 8, lineHeight: 1.6, marginBottom: 0 }}>
              <li>🚖 <a href="/heathrow-fare-compare">Compare taxi fares</a></li>
              <li>🚆 <a href="/train-vs-taxi">Train vs taxi</a></li>
              <li>🅿️ <a href="/heathrow-parking-vs-taxi">Parking vs taxi</a></li>
              <li>✈️ <a href="/flight-checker">Check your flight</a></li>
              <li>🕒 <a href="/leave-for-heathrow">When to leave for Heathrow</a></li>
              <li>🧭 <a href="/terminalFinder">Find your terminal</a></li>
              <li>🧳 <a href="/arrival-assistant">Arrival assistant</a></li>
            </ul>
            <div style={{ marginTop: 10, fontSize: 12, color: "#6b7280" }}>
              Last updated: {new Date().toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}
            </div>
          </div>
        </div>
      )}

      <div style={{ marginTop: 40 }}>
        <ReviewsBar />
      </div>
    </div>
  );
}
